import { BUTTON_I18N_TAGS, MowerPassability } from '@core/helpers/coreEnums';
import {
  generatePolybooligonFromRegions,
  getObjectPolybooligon,
  getPointFromPosition,
} from '@core/libs/automation/polygons/utils/polyboolTools';
import { FabricIcon, FabricIconTypes } from '@core/libs/fabric-icon-buttons/fabricIconButton';
import ReduxStore from '@core/libs/store/reduxStore';
import { segHaveIntersection } from '@core/libs/vector-calculus/intersections';
import { Layer } from '@sharedGlobal/helpers/enums/layer';
import { slugIndex } from '@sharedGlobal/helpers/slugIndex';
import { cloneDeep } from 'lodash-es';
import PolyBool from 'polybooljs';

import { ModalTools } from '../../../shared/helpers/modalTools.js';
import MontanaAPI from '../../../shared/libs/montanaAPI.js';

const SLUGS_TO_IGNORE = ['gras'];

const INTERSECTON_NOTICE_STATIC_PROPS = Object.freeze({
  slug: slugIndex.RoboticMowers.guideWireIntersectionNotice,
  layerDepth: 3399,
  layer: Layer.RoboticMowers,
  perPixelTargetFind: false,
});

const getIntersectionNotice = ({ x, y }) => {
  const button = new FabricIcon(FabricIconTypes.info, {
    ...INTERSECTON_NOTICE_STATIC_PROPS,
    left: x,
    top: y,
    scaleX: 3,
    scaleY: 3,
    donotRemove: true,
    callback: () => {
      ModalTools.init({
        body: 'robotic-mower.modals.guide-wire.intersection',
        buttons: [{ i18n: BUTTON_I18N_TAGS.CLOSE }],
      });
    },
  });

  return button;
};

export const clearGuideWireIntersectionNotices = () =>
  MontanaAPI.objects.remove(MontanaAPI.objects.getBySlug(slugIndex.RoboticMowers.guideWireIntersectionNotice));

export const addGuideWireIntersectionNotices = (guideWirePoints) => {
  guideWirePoints = guideWirePoints ?? cloneDeep(ReduxStore.state.roboticMower.guideWire);
  clearGuideWireIntersectionNotices();

  const guideWireSegments = guideWirePoints.map(getPointFromPosition).reduce((segments, end, index, array) => {
    if (0 < index) {
      segments.push({ start: array.at(index - 1), end });
    }
    return segments;
  }, []);

  // we will test all objects for now, filtering Objects included inside the garden might improve performance, but there will only
  // be a couple of objects with MowerPassability.Passable, they most likely are set manually.
  const gardenObjects = MontanaAPI.objects
    .getGardenObjects()
    .filter(({ mowerPassable, slug }) => !SLUGS_TO_IGNORE.includes(slug?.toLowerCase()) && mowerPassable === MowerPassability.Passable);

  const objectsWithSegmentsAndCenters = gardenObjects.map((object) => ({
    segments: PolyBool.segments(getObjectPolybooligon(object) ?? generatePolybooligonFromRegions())?.segments ?? [],
    center: object.getCenterPoint(),
  }));

  const centerPositionsForNotices = objectsWithSegmentsAndCenters.reduce((results, { segments, center }) => {
    const isIntersecting = guideWireSegments.some((guideWireSegment) =>
      segments.some((segment) => segHaveIntersection(guideWireSegment, segment))
    );
    if (isIntersecting) {
      results.push(center);
    }
    return results;
  }, []);

  const guideWireIntersectionNotices = centerPositionsForNotices.map(getIntersectionNotice);
  MontanaAPI.getCanvas().add(...guideWireIntersectionNotices);
  MontanaAPI.getCanvas().requestRenderAll();
};
